import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { rechargeAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Search, MessageSquare, CheckCircle, Clock, AlertCircle, IndianRupee, Settings, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';

const complaintTypeLabels = {
  not_received: 'Recharge not received',
  wrong_provider_id: 'Provide correct provider ID',
  wrong_amount: 'Wrong amount recharged',
  other: 'Other',
};

const Complaints = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');

  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminReply, setAdminReply] = useState('');
  const [updating, setUpdating] = useState(false);

  // Recharge action states
  const [rechargeStatus, setRechargeStatus] = useState('');
  const [operatorIdEdit, setOperatorIdEdit] = useState('');
  const [rechargeUpdating, setRechargeUpdating] = useState(false);
  const [checkingLive, setCheckingLive] = useState(false);

  const fetchComplaints = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const r = await api.get('/complaints', { params });
      setComplaints(r.data || []);
    } catch { toast.error('Failed to load complaints'); }
    setLoading(false);
  };

  useEffect(() => { fetchComplaints(); }, [statusFilter]);

  // Auto refresh
  useEffect(() => {
    const iv = setInterval(fetchComplaints, 10000);
    return () => clearInterval(iv);
  }, [statusFilter]);

  const openDetail = (c) => {
    setSelectedComplaint(c);
    setNewStatus(c.status);
    setAdminReply(c.admin_reply || '');
    setRechargeStatus(c.recharge_status || '');
    setOperatorIdEdit(c.operator_id || '');
    setDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedComplaint) return;
    setUpdating(true);
    try {
      await api.put(`/complaints/${selectedComplaint.id}`, {
        status: newStatus,
        admin_reply: adminReply,
      });
      toast.success('Complaint updated');
      setDialogOpen(false);
      fetchComplaints();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
    setUpdating(false);
  };

  const handleRechargeAction = async () => {
    if (!selectedComplaint) return;
    const rechargeId = selectedComplaint.recharge_id || selectedComplaint.reference_id;
    if (!rechargeId) { toast.error('No recharge linked'); return; }
    setRechargeUpdating(true);
    try {
      const payload = {
        status: rechargeStatus || selectedComplaint.recharge_status,
      };
      if (operatorIdEdit !== (selectedComplaint.operator_id || '')) {
        payload.operator_id = operatorIdEdit;
      }
      const res = await rechargeAPI.manualStatusChange(rechargeId, payload);
      toast.success(res.data?.message || 'Recharge updated');
      fetchComplaints();
      setSelectedComplaint(prev => ({
        ...prev,
        recharge_status: payload.status,
        operator_id: operatorIdEdit || prev.operator_id,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recharge update failed');
    }
    setRechargeUpdating(false);
  };

  const handleCheckLiveStatus = async () => {
    if (!selectedComplaint) return;
    const rechargeId = selectedComplaint.recharge_id || selectedComplaint.reference_id;
    if (!rechargeId) { toast.error('No recharge linked'); return; }
    setCheckingLive(true);
    try {
      const res = await rechargeAPI.checkStatus(rechargeId);
      const d = res.data;
      if (d.changed) {
        toast.success(`Status changed: ${d.old_status} → ${d.new_status}`);
        setRechargeStatus(d.new_status);
        setSelectedComplaint(prev => ({ ...prev, recharge_status: d.new_status, operator_id: d.api_response?.OPERATORID || prev.operator_id }));
        if (d.api_response?.OPERATORID) setOperatorIdEdit(d.api_response.OPERATORID);
        fetchComplaints();
      } else {
        toast.info(`Live status: ${d.new_status} (no change)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Status check failed');
    }
    setCheckingLive(false);
  };

  const getStatusBadge = (status) => {
    const map = {
      open: <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">OPEN</Badge>,
      in_progress: <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">IN PROGRESS</Badge>,
      resolved: <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">RESOLVED</Badge>,
    };
    return map[status] || <Badge variant="outline" className="text-[10px]">{status?.toUpperCase()}</Badge>;
  };

  const filtered = complaints.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.user_name || '').toLowerCase().includes(s) || (c.recharge_mobile || '').includes(s) || (c.subject || '').toLowerCase().includes(s);
  });

  const stats = {
    open: complaints.filter(c => c.status === 'open').length,
    in_progress: complaints.filter(c => c.status === 'in_progress').length,
    resolved: complaints.filter(c => c.status === 'resolved').length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="complaints-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">Complaints</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Manage recharge complaints</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-red-200 bg-red-50/50 cursor-pointer" onClick={() => setStatusFilter(statusFilter === 'open' ? '' : 'open')}>
            <CardContent className="p-3">
              <p className="text-[10px] font-bold text-red-600 uppercase">Open</p>
              <p className="text-2xl font-black text-red-700 font-mono">{stats.open}</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/50 cursor-pointer" onClick={() => setStatusFilter(statusFilter === 'in_progress' ? '' : 'in_progress')}>
            <CardContent className="p-3">
              <p className="text-[10px] font-bold text-amber-600 uppercase">In Progress</p>
              <p className="text-2xl font-black text-amber-700 font-mono">{stats.in_progress}</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/50 cursor-pointer" onClick={() => setStatusFilter(statusFilter === 'resolved' ? '' : 'resolved')}>
            <CardContent className="p-3">
              <p className="text-[10px] font-bold text-emerald-600 uppercase">Resolved</p>
              <p className="text-2xl font-black text-emerald-700 font-mono">{stats.resolved}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Search user, mobile..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" data-testid="complaint-search" />
          </div>
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[130px] h-8 text-xs"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1b2838] hover:bg-[#1b2838]">
                  <TableHead className="text-white text-[10px] font-bold py-2 px-2">User</TableHead>
                  <TableHead className="text-white text-[10px] font-bold py-2 px-2">Mobile</TableHead>
                  <TableHead className="text-white text-[10px] font-bold py-2 px-2">Operator</TableHead>
                  <TableHead className="text-white text-[10px] font-bold py-2 px-2">Amount</TableHead>
                  <TableHead className="text-white text-[10px] font-bold py-2 px-2">Operator Ref</TableHead>
                  <TableHead className="text-white text-[10px] font-bold py-2 px-2">Complaint Type</TableHead>
                  <TableHead className="text-white text-[10px] font-bold py-2 px-2">Status</TableHead>
                  <TableHead className="text-white text-[10px] font-bold py-2 px-2">Date</TableHead>
                  <TableHead className="text-white text-[10px] font-bold py-2 px-2">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground text-xs">No complaints found</TableCell></TableRow>
                ) : (
                  filtered.map((c, i) => (
                    <TableRow key={c.id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <TableCell className="text-[10px] py-2 px-2">
                        <div className="font-semibold">{c.user_name}</div>
                        {c.user_phone && <div className="text-muted-foreground">{c.user_phone}</div>}
                      </TableCell>
                      <TableCell className="text-[10px] py-2 px-2 font-mono">{c.recharge_mobile || '-'}</TableCell>
                      <TableCell className="text-[10px] py-2 px-2 uppercase font-semibold">{c.recharge_operator || '-'}</TableCell>
                      <TableCell className="text-[10px] py-2 px-2 font-mono font-semibold">{c.recharge_amount ? `₹${c.recharge_amount}` : '-'}</TableCell>
                      <TableCell className="text-[10px] py-2 px-2 font-mono">{c.operator_id || '-'}</TableCell>
                      <TableCell className="text-[10px] py-2 px-2">{complaintTypeLabels[c.complaint_type] || c.complaint_type}</TableCell>
                      <TableCell className="text-[10px] py-2 px-2">{getStatusBadge(c.status)}</TableCell>
                      <TableCell className="text-[10px] py-2 px-2 whitespace-nowrap">{c.created_at ? format(new Date(c.created_at), 'dd/MM/yy HH:mm') : '-'}</TableCell>
                      <TableCell className="text-[10px] py-2 px-2">
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-violet-600" onClick={() => openDetail(c)} data-testid={`view-complaint-${c.id}`}>
                          <MessageSquare className="w-3 h-3 mr-1" /> View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Detail + Status Change Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="complaint-detail-dialog">
            <DialogHeader>
              <DialogTitle>Complaint Details</DialogTitle>
              <DialogDescription>{selectedComplaint?.subject}</DialogDescription>
            </DialogHeader>
            {selectedComplaint && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-1.5 text-xs">
                  <div className="p-1.5 bg-muted rounded"><span className="text-muted-foreground block text-[9px]">User</span><span className="font-semibold text-[11px]">{selectedComplaint.user_name}</span></div>
                  <div className="p-1.5 bg-muted rounded"><span className="text-muted-foreground block text-[9px]">Mobile</span><span className="font-mono text-[11px]">{selectedComplaint.recharge_mobile || '-'}</span></div>
                  <div className="p-1.5 bg-muted rounded"><span className="text-muted-foreground block text-[9px]">Operator</span><span className="uppercase font-semibold text-[11px]">{selectedComplaint.recharge_operator || '-'}</span></div>
                  <div className="p-1.5 bg-muted rounded"><span className="text-muted-foreground block text-[9px]">Amount</span><span className="font-mono font-bold text-[11px]">₹{selectedComplaint.recharge_amount || 0}</span></div>
                  <div className="p-1.5 bg-muted rounded"><span className="text-muted-foreground block text-[9px]">{selectedComplaint.recharge_operator?.toLowerCase().includes('google') ? 'Redeem Code' : 'Operator Ref'}</span><code className="text-[10px]">{selectedComplaint.operator_id || '-'}</code></div>
                  <div className="p-1.5 bg-muted rounded"><span className="text-muted-foreground block text-[9px]">Recharge Status</span><span className="uppercase text-[10px] font-bold">{selectedComplaint.recharge_status || '-'}</span></div>
                </div>
                <div className="p-1.5 bg-muted rounded text-xs"><span className="text-muted-foreground block text-[9px]">Complaint Type</span><span className="font-semibold text-[11px]">{complaintTypeLabels[selectedComplaint.complaint_type] || selectedComplaint.complaint_type}</span></div>

                <div className="p-2 bg-slate-50 border rounded text-xs max-h-[80px] overflow-y-auto">
                  <p className="text-muted-foreground text-[9px] mb-0.5 font-bold">Message</p>
                  <pre className="whitespace-pre-wrap text-[11px]">{selectedComplaint.message}</pre>
                </div>

                {isAdmin && (
                  <>
                    {/* Recharge Action Section */}
                    {selectedComplaint.recharge_status && (
                      <div className="p-3 bg-violet-50/50 border border-violet-200 rounded-lg space-y-3">
                        <p className="text-[11px] font-bold text-violet-700 flex items-center gap-1.5"><Settings className="w-3.5 h-3.5" /> Recharge Action</p>
                        <Button size="sm" variant="outline" onClick={handleCheckLiveStatus} disabled={checkingLive} className="w-full h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="check-live-status-btn">
                          {checkingLive ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                          {checkingLive ? 'Checking...' : 'Check Live Status from API'}
                        </Button>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-[10px]">Recharge Status</Label>
                            <Select value={rechargeStatus} onValueChange={setRechargeStatus}>
                              <SelectTrigger className="h-8 text-xs" data-testid="recharge-status-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="success">Success</SelectItem>
                                <SelectItem value="failed">Failed</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="refund">Refund</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px]">Operator ID</Label>
                            <Input value={operatorIdEdit} onChange={e => setOperatorIdEdit(e.target.value)} className="h-8 text-xs font-mono" placeholder="Enter Operator ID" data-testid="operator-id-input" />
                          </div>
                        </div>
                        {(rechargeStatus === 'failed' || rechargeStatus === 'refund') && selectedComplaint.recharge_status === 'success' && (
                          <div className="text-[10px] p-2 bg-amber-50 border border-amber-200 rounded text-amber-800">
                            <IndianRupee className="w-3 h-3 inline mr-1" />Commission will be reversed & <strong>₹{selectedComplaint.recharge_amount}</strong> will be <strong>refunded</strong> to wallet
                          </div>
                        )}
                        {rechargeStatus === 'success' && selectedComplaint.recharge_status !== 'success' && (
                          <div className="text-[10px] p-2 bg-blue-50 border border-blue-200 rounded text-blue-800">
                            <IndianRupee className="w-3 h-3 inline mr-1" /><strong>₹{selectedComplaint.recharge_amount}</strong> will be deducted & commission will be added
                          </div>
                        )}
                        <Button size="sm" onClick={handleRechargeAction} disabled={rechargeUpdating} className="w-full h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white" data-testid="recharge-action-btn">
                          {rechargeUpdating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                          {rechargeUpdating ? 'Updating Recharge...' : 'Update Recharge'}
                        </Button>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger data-testid="complaint-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open"><span className="flex items-center gap-2"><AlertCircle className="w-3 h-3 text-red-500" /> Open</span></SelectItem>
                          <SelectItem value="in_progress"><span className="flex items-center gap-2"><Clock className="w-3 h-3 text-amber-500" /> In Progress</span></SelectItem>
                          <SelectItem value="resolved"><span className="flex items-center gap-2"><CheckCircle className="w-3 h-3 text-emerald-500" /> Resolved</span></SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Admin Reply</Label>
                      <textarea
                        value={adminReply}
                        onChange={e => setAdminReply(e.target.value)}
                        placeholder="Reply to the complaint..."
                        className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                        data-testid="admin-reply-input"
                      />
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleUpdate} disabled={updating} className="bg-[#4680ff] hover:bg-[#2563eb] text-white" data-testid="update-complaint-btn">
                        {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        {updating ? 'Updating...' : 'Update'}
                      </Button>
                    </DialogFooter>
                  </>
                )}

                {!isAdmin && selectedComplaint.admin_reply && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                    <p className="text-blue-600 text-[10px] mb-1 font-bold">Admin Reply</p>
                    <p>{selectedComplaint.admin_reply}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Complaints;
